// Firebase 관련 설정과 초기화
import { initializeApp } from 'firebase/app';
import { getFirestore, doc, setDoc, getDoc, getDocFromServer, updateDoc, increment } from 'firebase/firestore';
import { initializeAuth, browserLocalPersistence } from 'firebase/auth';

// Firebase 구성 정보
const firebaseConfig = {
  apiKey: "AIzaSyArzIY7tDvQaWmLzVf2LehucYJRO94MKfA",
  authDomain: "jambdomain-c5d3c.firebaseapp.com",
  projectId: "jambdomain-c5d3c",
  storageBucket: "jambdomain-c5d3c.appspot.com",
  messagingSenderId: "968408247435",
  appId: "1:968408247435:web:308a97b5b7cca0b425b88e"
};

// Firebase 초기화
const app = initializeApp(firebaseConfig);
const firestoreDB = getFirestore(app);
let firebaseAuth;

try {
  // Auth 초기화 with 영구 설정
  firebaseAuth = initializeAuth(app, {
    persistence: browserLocalPersistence
  });
} catch (error) {
  console.error('Firebase 초기화 중 오류 발생:', error);
  throw new Error('Firebase 초기화 실패');
}

// Firebase 서비스 내보내기
export const database = firestoreDB;
export const auth = firebaseAuth;

/**
 * URL 코드를 문서 ID로 변환하는 함수
 * 저장/조회 모두 "원문 코드 → NFC 정규화 → encodeURIComponent" 한 가지 규칙만 사용
 */
const encodeUrlCode = (code: string): string => {
  return encodeURIComponent(code.trim().normalize('NFC'));
};

/**
 * 주소창 경로(브라우저가 퍼센트 인코딩한 값)를 원문 코드로 복원
 */
const decodePathCode = (pathCode: string): string => {
  try {
    return decodeURIComponent(pathCode);
  } catch {
    return pathCode;
  }
};

// 이전 버전의 인코딩 규칙 (기존에 저장된 문서 조회용)
const legacyEncodeUrlCode = (code: string): string => {
  if (/^%[0-9A-F]{2}/i.test(code) || code.includes('%ED')) {
    return code;
  }
  return encodeURIComponent(code.trim());
};

// 커스텀 코드에 쓸 수 없는 문자 (경로/쿼리/해시 구분자, % 등)
const INVALID_CODE_CHARS = /[/?#%\\\s]/;

/**
 * URL을 단축합니다.
 * @param originalUrl 원본 URL
 * @param customCode 커스텀 코드 (선택사항)
 * @returns 생성된 단축 URL 정보
 */
export const shortenUrl = async (originalUrl: string, customCode?: string) => {
  try {
    // URL 형식 검증 및 수정
    let formattedUrl = originalUrl.trim();
    if (!formattedUrl || /\s/.test(formattedUrl)) {
      throw new Error('공백 없이 올바른 URL을 입력해주세요.');
    }
    const isHostWithPort = /^[^\s/:?#]+:\d+(?:[/?#]|$)/.test(formattedUrl);
    if (/^[a-z][a-z\d+.-]*:/i.test(formattedUrl) && !/^https?:\/\//i.test(formattedUrl) && !isHostWithPort) {
      throw new Error('http:// 또는 https:// 웹 주소를 입력해주세요.');
    }
    if (!formattedUrl.match(/^https?:\/\//i)) {
      formattedUrl = 'https://' + formattedUrl;
    }

    // URL 유효성 검사
    try {
      const parsedUrl = new URL(formattedUrl);
      if (!parsedUrl.hostname || parsedUrl.username || parsedUrl.password) {
        throw new Error('Invalid URL');
      }
    } catch (error) {
      throw new Error('유효하지 않은 URL입니다.');
    }

    const trimmedCustom = customCode?.trim().normalize('NFC');
    if (trimmedCustom && INVALID_CODE_CHARS.test(trimmedCustom)) {
      throw new Error('커스텀 URL에는 공백, /, ?, #, %, \\ 를 사용할 수 없습니다.');
    }
    if (trimmedCustom && (trimmedCustom === '.' || trimmedCustom === '..' || /^__.*__$/.test(trimmedCustom) ||
      ['teacher', 'student', 'index.html'].includes(trimmedCustom.toLowerCase()))) {
      throw new Error('사용할 수 없는 주소입니다. 다른 이름을 입력해주세요.');
    }
    if (trimmedCustom && Array.from(trimmedCustom).length > 60) {
      throw new Error('나만의 주소는 60자 이내로 입력해주세요.');
    }

    const urlCode = trimmedCustom || generateUrlCode();
    const encodedCode = encodeUrlCode(urlCode);
    
    const urlRef = doc(database, 'urls', encodedCode);

    // 중복 체크
    const existing = await getDoc(urlRef);
    if (existing.exists()) {
      throw new Error('이미 사용 중인 코드입니다.');
    }

    // 원본 코드와 인코딩된 코드 모두 저장
    await setDoc(urlRef, {
      originalUrl: formattedUrl,
      createdAt: Date.now(),
      clicks: 0,
      urlCode: urlCode,
      encodedCode: encodedCode
    });

    return {
      shortUrl: `https://jamb.kr/${urlCode}`,
      urlCode,
    };
  } catch (error) {
    console.error('URL 단축 중 오류:', error);
    throw error;
  }
};

/**
 * 단축 URL 코드로 원본 URL을 조회합니다.
 * @param urlCode URL 코드
 * @returns 원본 URL 정보
 */
export const getOriginalUrl = async (urlCode: string) => {
  try {
    const encodedCode = encodeUrlCode(decodePathCode(urlCode));

    let urlRef = doc(database, 'urls', encodedCode);
    let docSnap = await getDoc(urlRef);

    // 새 규칙으로 못 찾으면 이전 규칙으로 한 번 더 조회
    const legacyCode = legacyEncodeUrlCode(urlCode);
    if (!docSnap.exists() && legacyCode !== encodedCode) {
      urlRef = doc(database, 'urls', legacyCode);
      docSnap = await getDoc(urlRef);
    }

    if (!docSnap.exists()) {
      throw new Error('존재하지 않는 URL입니다.');
    }

    const data = docSnap.data();
    
    if (typeof data.originalUrl !== 'string' || !/^https?:\/\//i.test(data.originalUrl)) {
      throw new Error('올바르지 않은 목적지 주소입니다.');
    }

    // 클릭 수 증가 (비동기로 처리)
    updateDoc(urlRef, { clicks: increment(1) })
      .catch(error => console.error('클릭 수 업데이트 실패:', error));
    
    return data.originalUrl;
  } catch (error) {
    console.error('URL 조회 중 오류:', error);
    throw error;
  }
};

/**
 * 3자리 대문자 URL 코드를 생성합니다.
 */
const generateUrlCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = '';
  for (let i = 0; i < 3; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

/**
 * Firebase 연결 상태를 테스트합니다.
 */
export const testDatabaseConnection = async () => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const testRef = doc(database, 'connection-test', 'test');
    // 상태 표시를 위한 서버 읽기만 수행합니다. 방문할 때마다 쓰지 않습니다.
    await Promise.race([
      getDocFromServer(testRef),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('연결 확인 시간 초과')), 8000);
      }),
    ]);
    return true;
  } catch (error) {
    console.error('데이터베이스 연결 테스트 실패:', error);
    return false;
  } finally {
    if (timer) clearTimeout(timer);
  }
};
